import React, { useMemo, useState } from 'react';
import { useAddUserManagementMutation } from 'src/api/mutations/userManagementMutation';
import { useUserManagementQuery } from 'src/api/queries/userManagementQueries';
import { planUpgradeRequired } from 'src/api/utils/errorHandlers';
import { useAlerts } from 'src/common/AlertManager';
import { Button, Checkbox, Input, Paper, Select } from 'src/components';
import { Info } from 'src/common/icons';
import BaseModal, { BaseModalProps } from 'src/components/BaseModal/BaseModal';

import useForm from 'src/hooks/useForm';
import CheckUsageDropDown from 'src/pages/CheckUsage/DropDown/DropDown';
import { useAuthUser } from 'react-auth-kit';
import { useHistory } from 'react-router-dom';
import {
  usePlansQuery,
  useSubscriptionQuery,
} from 'src/api/queries/subscriptionQueries';
import {
  PLAN_PICKER_ROUTE,
  SUBSCRIPTION_ROUTE,
} from 'src/admin-pages/Subscription/Subscription';
import { isSelfServePlan } from 'src/admin-pages/Subscription/planVisibility';

import * as yup from 'yup';

import styles from '../styles.module.scss';

import messages from '../messages';
import { FormattedMessage, useIntl } from 'react-intl';
import useModal from 'src/hooks/useModal';

// Roles that consume a CONTRIBUTOR seat. Mirrors CONTRIBUTOR_ROLE_IDS in
// filespin/core/subscription.py and the copy in Users.tsx.
const CONTRIBUTOR_ROLE_VALUES = ['1', '2', '4'];

// The read-only role. Named USER/VIEWER because "viewer seat" is the billing
// term everywhere else (the plan cards, the seat counts, the upsells) while the
// role itself is stored as USER — showing only one of the two names leaves the
// reader to guess they are the same thing.
const VIEWER_ROLE_VALUE = '3';

const options = [
  {
    label: 'ADMINISTRATOR (Manage Assets, Users & Settings)',
    value: '1',
  },
  {
    label: 'MANAGER (Manage Assets & Settings)',
    value: '2',
  },
  {
    label: 'CREATOR (Upload Assets & Manage Assets)',
    value: '4',
  },
  {
    label: 'USER/VIEWER (View & Download Assets)',
    value: VIEWER_ROLE_VALUE,
  },
];

// Fallback if the plan catalog isn't readable; matches the seats block on the
// Subscription page.
const SEAT_PRICE_FALLBACK_USD = 9;

const AddUserSchema = yup.object().shape({
  email: yup
    .string()
    .matches(/^\w+([.-]?\w+)*@(\w+)|((\.\w{2,})+)/g, 'is not a valid email')
    .required()
    .label('Email'),
  first_name: yup.string().required().label('First name'),
  last_name: yup.string().required().label('Last name'),
  role_id: yup.number().required().label('Role'),
  enabled: yup.boolean().label('Enabled'),
  notification_email: yup.boolean().label('Notification Email'),
  group_asset_access: yup.boolean().label('Group Asset Access'),
  template_user_id: yup.number().label('Template User'),
});

interface AddUserModalProps extends BaseModalProps {
  onAddUser: () => void;
  // Free CONTRIBUTOR seats, computed by the page that opens this modal:
  //   undefined -> unknown (subscription unreadable / pre-Phase-6) — do not gate
  //   Infinity  -> unlimited (Enterprise)
  //   <= 0      -> exhausted; contributor roles are disabled here
  // Passed in rather than recomputed so the page and the modal can never
  // disagree about how many seats are left, and so this modal never becomes
  // MORE permissive than the server gate.
  contributorSeatsLeft?: number;
}

const AddUserModal = (props: AddUserModalProps) => {
  const auth = useAuthUser();
  const authenticatedUser = auth();
  const history = useHistory();

  const { contributorSeatsLeft } = props;
  const seatsExhausted =
    contributorSeatsLeft !== undefined && contributorSeatsLeft <= 0;

  const { form, handleChange } = useForm({
    email: '',
    first_name: '',
    last_name: '',
    // Default to the only role that can actually be invited when every
    // contributor seat is taken. Opening on a disabled option would submit a
    // role the server refuses.
    role_id: seatsExhausted ? VIEWER_ROLE_VALUE : '1',
    enabled: true,
    notification_email: true,
    group_asset_access: false,
    template_user_id: String(authenticatedUser?.userId ?? ''),
  });

  const [errors, setErrors] = useState();
  const { closeModal } = useModal();

  const { showSuccess, showAlert } = useAlerts();

  const { formatMessage } = useIntl();

  const { data: usersData } = useUserManagementQuery({ page: 1, limit_per_page: 500 });

  // Both queries are already warm on this page (the list computes the seat
  // counts from the same subscription), so these are cache reads, not fetches.
  const { data: subscription } = useSubscriptionQuery({ retry: false });
  const { data: planCatalog } = usePlansQuery({ retry: false });

  const onTrial = Boolean(subscription?.details?.is_trial)
    || subscription?.status === 'trialing';
  const isOwner = Boolean(subscription)
    && Number(authenticatedUser?.userId) === Number(subscription?.owner_user_id);
  // Enterprise plans are invoiced under an agreement; there is no self-serve
  // seat to buy, so the CTA would lead nowhere.
  const selfServe = subscription ? isSelfServePlan(subscription) : false;
  const seatPriceUsd = planCatalog?.seat_price_usd ?? SEAT_PRICE_FALLBACK_USD;
  const contributorQuota = subscription?.usage?.users?.contributor_quota ?? 0;

  // Disable rather than hide: the reader can see which roles exist and the note
  // below says why they are unavailable.
  const roleOptions = useMemo(
    () => options.map((option) => ({
      ...option,
      disabled: seatsExhausted && CONTRIBUTOR_ROLE_VALUES.includes(option.value),
    })),
    [seatsExhausted],
  );

  const userOptions = useMemo(() => {
    const users = usersData?.users ?? [];
    return users.map((u: any) => ({
      key: String(u.id),
      value: `${u.email} (${u.role_name})`,
    }));
  }, [usersData]);

  const userMutation = useAddUserManagementMutation({
    onSuccess: () => {
      showSuccess(formatMessage(messages.userAddedSuccessfully));
      closeModal();
      props.onAddUser();
    },
    onError: (error: any) => {
      // Seat cap reached (plan-structure v2 Phase 6): the backend answers with
      // the structured plan_upgrade_required 403, whose `error` field is a
      // PARSED OBJECT — the legacy branch below would alert "[object Object]".
      const gate = planUpgradeRequired(error);
      if (gate) {
        showAlert(gate.message
          ?? `Upgrade to ${gate.requiredPlan} to add more team members.`);
        return;
      }
      if('error' in error) {
        //console.log(error.error);
        showAlert(error.error);
      }else{
        showAlert(error.message);
      }
    },
  });

  const handleCloseModal = () => closeModal();

  const handleSubmit: React.FormEventHandler<HTMLFormElement> = async (e) => {
    e.preventDefault();
    setErrors(undefined);
    try {
      await AddUserSchema.validate({
        ...form,
        role_id: Number(form.role_id),
        template_user_id: Number(form.template_user_id),
      });

      userMutation.mutateAsync({ ...form });
    } catch (error: any) {
      setErrors(error.message);
    }
  };

  return (
    <BaseModal {...props}>
      <h3>
        <FormattedMessage {...messages.newUserModalTitle} />
      </h3>

      <form onSubmit={handleSubmit}>
        <div style={{display:'flex',gap:'0.5rem',alignItems:'flex-start',padding:'0.75rem 1rem',marginBottom:'1rem',backgroundColor:'var(--color-prime-100, #e8ecfc)',borderRadius:'0.5rem',fontSize:'var(--font-size-s)',color:'var(--color-prime-400, #2e53e3)',lineHeight:'1.5'}}>
          <Info style={{flexShrink:0,marginTop:'0.15rem'}} />
          <span>The new user will receive a sign-up email with a link to set their password and complete onboarding.</span>
        </div>
        <Paper >
          <Input
            label="Email"
            type="email"
            variant="secondary"
            value={form.email}
            onChange={(value) => handleChange('email', value)}
          />

          <div className={styles.inlineGroup}>
            <Input
              label={formatMessage(messages.firstName)}
              variant="secondary"
              value={form.first_name}
              onChange={(value) => handleChange('first_name', value)}
            />
            <Input
              label={formatMessage(messages.lastName)}
              variant="secondary"
              value={form.last_name}
              onChange={(value) => handleChange('last_name', value)}
            />
          </div>

          <div style={{display:"flex",flexDirection:"column",marginBottom:'30px'}}>
              <p>
                <FormattedMessage {...messages.role} />
              </p>
              <Select options={roleOptions} value={form.role_id} onChange={(value) => handleChange('role_id', value)} marginTopstyle='-30px' />
              {/* Why the contributor roles are greyed out, and the one action
                  that changes it. Without this the disabled options read as a
                  bug — and the invite would fail the server's seat gate anyway. */}
              {seatsExhausted && (
                <div className={styles.seatNotice}>
                  <span>
                    <FormattedMessage
                      {...(onTrial
                        ? messages.seatsNoContributorSeatsTrial
                        : messages.seatsNoContributorSeats)}
                      values={{ quota: contributorQuota }}
                    />
                  </span>
                  {!isOwner ? (
                    <span className={styles.seatNoticeMuted}>
                      <FormattedMessage
                        {...messages.seatsOwnerOnly}
                        values={{ email: subscription?.owner_email ?? '' }}
                      />
                    </span>
                  ) : onTrial ? (
                    // A trial cannot buy seats — there is no card on file to
                    // prorate against — so it is sent to the plan picker.
                    <Button
                      type="button"
                      mode="secondary"
                      size="small"
                      onClick={() => { closeModal(); history.push(PLAN_PICKER_ROUTE); }}
                    >
                      <FormattedMessage {...messages.seatsChoosePlanCta} />
                    </Button>
                  ) : selfServe ? (
                    // Lands on the Subscription page with the seats control
                    // already open (see RailActions' `?purchase=` handling) —
                    // a CTA that drops you on a page of collapsed links is
                    // barely a CTA.
                    <Button
                      type="button"
                      mode="secondary"
                      size="small"
                      onClick={() => {
                        closeModal();
                        history.push(`${SUBSCRIPTION_ROUTE}?purchase=seats`);
                      }}
                    >
                      <FormattedMessage
                        {...messages.seatsBuyCta}
                        values={{ price: seatPriceUsd }}
                      />
                    </Button>
                  ) : null}
                </div>
              )}
            </div>

          <div style={{display:"flex",flexDirection:"column",marginBottom:'30px'}}>
            <p>Initialize settings from user</p>
            <CheckUsageDropDown
              options={userOptions}
              selectedKeys={[form.template_user_id]}
              onClick={(key) => handleChange('template_user_id', String(key))}
              placeholder="Select a user"
            />
            <span style={{fontSize:'var(--font-size-xs)',opacity:0.5,lineHeight:'1.5',marginTop:'0.5rem'}}>
              User inherits storage, watermark, image, video, webhook, database and other settings. Security settings such as API key will not be inherited.
            </span>
          </div>

          <div>
            <Checkbox
              label={
                <span>
                  <FormattedMessage {...messages.allowUserGroupAssets} />
                  <br />
                  <span style={{fontSize:'var(--font-size-xs)',opacity:0.6,fontWeight:'normal'}}>
                    <FormattedMessage {...messages.allowUserGroupAssetsHint} />
                  </span>
                </span>
              }
              checked={form.group_asset_access}
              onChangeCapture={() => handleChange('group_asset_access', !form.group_asset_access)}
              className={styles.formSpacing}
            />
          </div>
        </Paper>

        <Paper>
          {errors && <p className={styles.errorMessage}>{errors}</p>}
          <div className={styles.groupedButtons}>
            <Button type="submit" mode="primary" loading={userMutation.isLoading} size="small">
              Invite User
            </Button>
            <Button
              type="button"
              mode="tertiary"
              onClick={handleCloseModal}
              disabled={userMutation.isLoading}
              size="small"
            >
              <FormattedMessage {...messages.cancel} />
            </Button>
          </div>
        </Paper>
      </form>
    </BaseModal>
  );
};

export default AddUserModal;
